// Portal — the account overview: your legend, your worlds (each opens to its idle cities and quests to claim; Ben, Oct 19
// 2026: quests are no longer their own section) and the closed worlds (your result and the top fives).
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";
import type { QuestItem } from "@/api/types";
import { Page } from "@/app/Page";
import { art } from "@/kit/art";
import { Button, Card, CardHead, Empty, HeroCard, LinkButton, Row, RowText, Skeleton } from "@/kit/primitives";
import { keys } from "@/state/queries";
import { useNow } from "@/state/clock";
import { usePrompts } from "@/state/prompts";
import { useSession } from "@/state/session";
import { useLayout } from "@/state/layout";
import { fmtDate } from "@/game/time";
import { abbrev, fmtNum, plural } from "@/game/format";
import { usePortal, type WorldGlance } from "./usePortal";
import { WorldRow } from "./WorldRow";
import { ClosedWorldsCard } from "./ClosedWorlds";
import "./portal.css";
import { titleName } from "@/game/titles";
import { TitleBadge } from "@/screens/profile/TitleBadge";

export function Portal() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const prompts = usePrompts();
  const setWorld = useSession((s) => s.setWorld);
  const { init, glances, loading } = usePortal();
  const now = useNow(1000);
  // wide screens (2+ columns): two columns of the Portal's own — the hero and Closed worlds on the left, Your worlds on the
  // right — so nothing waits under the short hero for the long world list
  const wide = useLayout().cols >= 2;
  const [claiming, setClaiming] = useState<string | null>(null);

  const user = init.data?.user;
  const titles = user?.titles;
  const earned = titles?.earned_title_names?.length ?? 0;
  const mainTitle = titles?.title_main?.title ? titleName(String(titles.title_main.title), init.data?.titles_config?.details) : null;
  const worldsIn = init.data?.world?.worlds_in?.length ?? 0;
  const cities = glances.reduce((a, g) => a + g.cities, 0);
  const kills = glances.reduce((a, g) => a + g.kst, 0);
  const incomingTotal = glances.reduce((a, g) => a + g.incoming, 0);
  const earliestJoin = useMemo(() => {
    // the earliest "earned_in" stamp across titles is the oldest trace of the account we have
    const stamps: number[] = [];
    for (const tiers of Object.values(titles?.titles_earned ?? {})) {
      for (const t of Object.values(tiers)) for (const v of Object.values(t.earned_in ?? {})) if (typeof v === "number") stamps.push(v > 1e12 ? v : v * 1000);
    }
    return stamps.length ? Math.min(...stamps) : null;
  }, [titles]);

  const attention = glances.filter((g) => g.incoming > 0 || g.claimable.length > 0 || g.newReport || g.newMail).sort((a, b) => b.incoming - a.incoming);
  const quiet = glances.filter((g) => !attention.includes(g) && g.village);

  const goWorld = (g: WorldGlance) => {
    setWorld(g.wid);
    nav(`/w/${g.wid}`);
  };

  const claim = async (g: WorldGlance, q: QuestItem) => {
    if (!g.vid) return;
    setClaiming(`${g.wid}:${q.id}`);
    try {
      await api.quest.collect({ wid: g.wid, vid: g.vid }, q.id);
      prompts.toast(`${q.name} claimed on World ${g.wid}.`, "success");
      await qc.invalidateQueries({ queryKey: keys.village(g.wid, g.vid) });
    } catch (e) {
      prompts.error(e);
    } finally {
      setClaiming(null);
    }
  };

  const legend = (
    <>
      {loading && !init.data ? (
        <>
          <Skeleton h={292} />
          <Skeleton h={300} />
        </>
      ) : (
        <HeroCard
          art={art.hero.portal}
          artWidth="57%"
          title="Your legend"
          lines={[
            <>
              {user?.login ? <><b className="gold">{user.login}</b>, </> : null}
              lord of <b className="gold">{plural(worldsIn, "world")}</b>
              {earliestJoin ? <> since {fmtDate(earliestJoin, true)}</> : null}.
            </>,
            mainTitle ? <>Bearing the title {titles?.title_main?.title && <TitleBadge title={String(titles.title_main.title)} tier={Number(titles.title_main.tier) || 1} details={init.data?.titles_config?.details} size={22} className="lg-badge" />}<b className="gold">{mainTitle}</b>.</> : <>Nothing noteworthy.</>,
          ]}
          link={<LinkButton onClick={() => { const w = glances[0]?.wid ?? useSession.getState().wid; const uid = useSession.getState().uid; if (w && uid) nav(`/w/${w}/player/${uid}`); else prompts.toast("Join a world to see your career there."); }}>View career</LinkButton>}
          stats={[
            { label: "Worlds", value: fmtNum(worldsIn) },
            { label: "Cities", value: fmtNum(cities) },
            { label: "Kill score", value: abbrev(kills) },
            { label: "Titles", value: fmtNum(earned) },
          ]}
        />
      )}
    </>
  );
  const yourWorlds = (
    <Card className="pt-worlds">
      <CardHead title="Your worlds" aside={glances.length ? (attention.length ? `${attention.length} need attention` : "All quiet") : undefined} asideCaps />
      <div className="rows pw-list">
        {glances.filter((g) => g.loading).map((g) => <Skeleton key={g.wid} h={76} />)}
        {[...attention, ...quiet].map((g) => (
          <WorldRow key={g.wid} g={g} now={now} claiming={claiming} onClaim={claim} onGo={goWorld} onCity={(vid) => { setWorld(g.wid); nav(`/w/${g.wid}/city/${vid}/hall`); }} onQuests={() => { setWorld(g.wid); nav(`/w/${g.wid}/quests`); }} />
        ))}
        {glances.filter((g) => g.error).map((g) => (
          <Row key={g.wid}>
            <RowText eyebrow={`World ${g.wid}`} line="Couldn't read this world." meta="It will be tried again shortly." />
            <div className="right"><Button size="md" onClick={() => goWorld(g)}>Go to</Button></div>
          </Row>
        ))}
        {!glances.length && !loading && <Empty>You're not in any world yet. <button type="button" className="link" onClick={() => nav("/worlds")}>Join one</button></Empty>}
      </div>
      <div className="card-foot">
        <span />
        <LinkButton onClick={() => nav("/worlds")}>All worlds</LinkButton>
      </div>
    </Card>
  );

  return (
    <Page header={{ kind: "title", title: "PORTAL", subtitle: "Account overview" }} badges={{ portal: incomingTotal || undefined }}>
      <div className="stack">
        {wide ? (
          <div className="pt-wide">
            <div className="pt-side">
              {legend}
              <ClosedWorldsCard />
            </div>
            {yourWorlds}
          </div>
        ) : (
          <>
            {legend}
            {yourWorlds}
            <ClosedWorldsCard />
          </>
        )}
      </div>
    </Page>
  );
}
